/* Reading a deposit and withdrawal history out of a file.
 *
 * Money paid in is the one figure this book cannot derive from anything else.
 * Every position, price and contract in it is a fact about the market; what you
 * funded is a fact about you, and without it "up 12%" and "up 12% after putting
 * another eight thousand in" are the same number on the same chart. §5.1 needs
 * the flows and nothing else can stand in for them.
 *
 * Typing years of them by hand is the reason they were missing. This reads them
 * in one go, and the three decisions it makes are all the same decision — never
 * write down something the file did not say:
 *
 *   1. **A transfer is not a deposit.** Moving złoty into a dollar subaccount at
 *      the same broker adds nothing to the book. Counted as a transfer, listed,
 *      and kept out of the total. Getting this wrong is the classic way to
 *      report funding you never did — the same fault `convertCash.ts` exists to
 *      avoid, arriving through a different door.
 *   2. **An account it cannot recognise is not guessed at.** The book's accounts
 *      are named by the person who made them and a file exported somewhere else
 *      has its own words for them. Anything unmatched is offered for mapping and
 *      otherwise files as "across the book" — which §5.1 sums identically, so
 *      the total is right either way and no broker gets a deposit it never saw.
 *   3. **The date is the date as written.** Times are read as the instant they
 *      say. Shifting them by a timezone this file never states would move a
 *      deposit made at ten past midnight onto the day before, and the day is
 *      what decides which exchange rate converts it.
 *
 * Ids are derived from the row, so running the same file twice corrects the
 * entries rather than doubling the money paid in. */

import type { Account, AccountRecord, CashFlow } from '../types';
import { parseDelimited } from '../lib/csv';

export interface ParsedFlow {
  id: string;
  /* The USD value of one unit of `currency` on this flow's own day, when the
     file states it.
     *
     * A file that carries its own rates needs no lookups at all, which is the
     * difference between an import that finishes instantly and one that makes a
     * request per day and looks stuck. It is not a shortcut around §5.1: a rate
     * written in the file is a rate somebody recorded, and the rule was only
     * ever that the rate must be the one from the flow's own date — not that
     * this app has to be the one to fetch it. */
  rateToUsd?: number;
  /** The account as the file names it, before anything is mapped. */
  source: string;
  /** Positive in, negative out — the sign convention `CashFlow` already uses. */
  amount: number;
  currency: string;
  at: string;
  note?: string;
}

export interface CashFlowImport {
  flows: ParsedFlow[];
  /** Rows that move money about without adding any to the book. */
  transfers: Array<{ line: number; what: string }>;
  skipped: Array<{ line: number; why: string }>;
  /** Every distinct account name the file used, in the order first seen. */
  sources: string[];
}

const EMPTY: CashFlowImport = { flows: [], transfers: [], skipped: [], sources: [] };

/* A file may arrive as the whole document it was written in. The block fenced
   as csv is the part meant for a machine, so it is preferred when present —
   pasting the notes around it is not a mistake anybody should have to undo. */
export function csvBlock(text: string): string {
  const fenced = /```\s*csv\s*\r?\n([\s\S]*?)```/i.exec(text);
  return fenced?.[1] ?? text;
}

function toNumber(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  // Thousands written with a space or a comma, decimals with either separator.
  const cleaned = raw.replace(/[\s ]/g, '').replace(/,(\d{3}\b)/g, '$1').replace(',', '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** `2026-04-18` and `02:41` become one instant, with the date left as written —
    see the note at the top about why it is not shifted. */
function toIso(date: string, time: string | undefined): string | null {
  const day = date.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const clock = /^\d{1,2}:\d{2}/.exec((time ?? '').trim())?.[0];
  const [h = '12', m = '00'] = clock ? clock.split(':') : [];
  const iso = `${day}T${h.padStart(2, '0')}:${m}:00.000Z`;
  return Number.isNaN(new Date(iso).getTime()) ? null : iso;
}

/** Stable across re-imports: the same row read twice produces the same id, so
    running a file again corrects the entries instead of doubling them. */
export function flowId(f: Pick<ParsedFlow, 'source' | 'at' | 'currency' | 'amount'>): string {
  const slug = f.source.toLowerCase().replace(/[^a-z0-9]+/g, '_') || 'book';
  return `cf-${slug}-${f.at.slice(0, 16)}-${f.currency.toUpperCase()}-${f.amount.toFixed(2)}`;
}

/* Which of the book's accounts a file's word for one means.
 *
 * Only answers where the answer is not a guess: the id or the name, matched
 * once punctuation and case are taken out of the question. `IBKR` finds `ibkr`
 * and `REVOLUT_BROKERAGE` finds `revolut` because one contains the other.
 * `XTB_PLN` against a book holding `xtb_usd` and `xtb_ike` finds neither, and
 * that is the correct answer — the file and the book disagree about what the
 * account is called, and only the person who named it can settle it. */
export function guessAccount(source: string, accounts: AccountRecord[]): Account | undefined {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const want = norm(source);
  if (want === '') return undefined;

  const exact = accounts.find((a) => norm(a.id) === want || norm(a.name) === want);
  if (exact) return exact.id;

  const contained = accounts.filter((a) => want.startsWith(norm(a.id)) || want.startsWith(norm(a.name)));
  // Only when it is unambiguous: two accounts matching means the file is not
  // telling us which, and picking one would be inventing the answer.
  return contained.length === 1 ? contained[0]!.id : undefined;
}

const HEADERS = ['date', 'time', 'account', 'type', 'currency', 'amount', 'rate_to_usd', 'note'] as const;

export function readCashFlows(text: string): CashFlowImport {
  const rows = parseDelimited(csvBlock(text)).filter((r) => r.some((c) => c.trim() !== ''));
  if (rows.length < 2) return EMPTY;

  /* Read by header name rather than by position. A column added or moved in the
     middle of a file is not a reason to file every amount under the wrong
     heading, and the alternative is a mapping step nobody can check by eye. */
  const head = rows[0]!.map((c) => c.trim().toLowerCase());
  const at = (name: (typeof HEADERS)[number]) => head.indexOf(name);
  const col = { date: at('date'), time: at('time'), account: at('account'), type: at('type'), currency: at('currency'), amount: at('amount'), rate: at('rate_to_usd'), note: at('note') };
  if (col.date < 0 || col.type < 0 || col.amount < 0 || col.currency < 0) return EMPTY;

  const flows: ParsedFlow[] = [];
  const transfers: CashFlowImport['transfers'] = [];
  const skipped: CashFlowImport['skipped'] = [];
  const sources: string[] = [];

  rows.slice(1).forEach((row, i) => {
    const line = i + 2;
    const cell = (c: number) => (c >= 0 ? (row[c] ?? '').trim() : '');
    const source = cell(col.account);
    const kind = cell(col.type).toUpperCase();
    const iso = toIso(cell(col.date), cell(col.time));
    const value = toNumber(cell(col.amount));
    const currency = cell(col.currency).toUpperCase();

    if (iso === null) return void skipped.push({ line, why: `no date could be read from "${cell(col.date)}"` });
    if (value === null || value === 0) return void skipped.push({ line, why: 'no amount on the row' });
    if (currency === '') return void skipped.push({ line, why: 'no currency on the row' });

    if (kind === 'FX_SWAP' || kind === 'TRANSFER' || kind === 'SWAP') {
      transfers.push({ line, what: `${iso.slice(0, 10)} · ${value} ${currency}${source ? ` · ${source}` : ''}` });
      return;
    }
    if (kind !== 'DEPOSIT' && kind !== 'WITHDRAWAL') {
      return void skipped.push({ line, why: `"${cell(col.type)}" is not a deposit, a withdrawal or a transfer` });
    }

    /* The size is the size; the direction is the type. A file that writes
       withdrawals as negatives as well would otherwise turn one into a deposit. */
    const amount = kind === 'WITHDRAWAL' ? -Math.abs(value) : Math.abs(value);
    const flow: ParsedFlow = { id: '', source, amount, currency, at: iso };
    flow.id = flowId(flow);
    const note = cell(col.note);
    if (note !== '') flow.note = note;
    // A rate of zero is not a rate; it would convert the flow to nothing.
    const rate = toNumber(cell(col.rate));
    if (rate !== null && rate > 0) flow.rateToUsd = rate;

    flows.push(flow);
    if (source !== '' && !sources.includes(source)) sources.push(source);
  });

  return { flows, transfers, skipped, sources };
}

/** What the file adds up to, before any of it is written, so the preview can be
    checked against the totals the file itself states. */
export function totalsByCurrency(flows: ParsedFlow[]): Array<{ currency: string; in: number; out: number }> {
  const by = new Map<string, { currency: string; in: number; out: number }>();
  for (const f of flows) {
    const row = by.get(f.currency) ?? { currency: f.currency, in: 0, out: 0 };
    if (f.amount >= 0) row.in += f.amount; else row.out += -f.amount;
    by.set(f.currency, row);
  }
  return [...by.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/** Every date whose flows need a rate looking up — one per day, not one per
    row, since a day has one table. */
export function ratesNeeded(flows: ParsedFlow[]): string[] {
  const days = new Set<string>();
  for (const f of flows) {
    if (f.currency === 'USD' || f.currency === 'USDT') continue;
    // A row that brought its own rate is not a day anybody has to ask about.
    if (f.rateToUsd !== undefined) continue;
    days.add(f.at.slice(0, 10));
  }
  return [...days].sort();
}

/** The records as the book stores them. `rates` maps a day to the USD value of
    one unit of that day's currency; a day with no rate is written without one,
    which the book already renders as converting at the current rate. */
export function toCashFlows(
  flows: ParsedFlow[],
  mapping: Record<string, Account | ''>,
  rates: Map<string, number>,
): CashFlow[] {
  return flows.map((f) => {
    const out: CashFlow = { id: f.id, amount: f.amount, currency: f.currency, at: f.at };
    const account = mapping[f.source];
    if (account) out.account = account;
    if (f.note) out.note = f.note;

    if (f.currency === 'USD' || f.currency === 'USDT') out.rateToUsd = 1;
    else if (f.rateToUsd !== undefined) out.rateToUsd = f.rateToUsd;
    else {
      const rate = rates.get(`${f.at.slice(0, 10)}|${f.currency}`);
      if (rate !== undefined) out.rateToUsd = rate;
    }
    return out;
  });
}
